import React, { useCallback, useEffect, useMemo, useState } from "react";
import swal from "sweetalert";
import {
  PERMISSION_GROUPS,
  flattenGroupItems,
} from "../Permission/permissionGroups";
import {
  buildTargetRoleIds,
  fetchPermissionGroups,
  normalizePermissionGroupIds,
  syncUserRoleMembership,
} from "../Permission/employeeRoleAssignment";
import { fetchUserPermissionsForUser } from "../../utilis/userPermissions";
import { isPermissionEnabled } from "../../utilis/permissionStorage";
import { deriveLegacyApplicationAccessFlags } from "../../utilis/applicationModuleAccess";
import { FIELD_CLASS, INPUT_CLASS, LABEL_CLASS } from "./accountFormShared";

function enabledPermissionLabels(permissions) {
  if (!permissions || typeof permissions !== "object") return [];
  const labels = [];
  PERMISSION_GROUPS.forEach((group) => {
    flattenGroupItems(group).forEach((item) => {
      if (isPermissionEnabled(permissions[item.key])) {
        labels.push({ group: group.title, label: item.label, key: item.key });
      }
    });
  });
  return labels;
}

/**
 * Role assignment + effective permissions (read-only) for employee account modal.
 */
export default function EmployeeAccessRoles({
  userId,
  disabled = false,
  onRolesChange,
  onLegacyAccessDerived,
}) {
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [primaryRoleId, setPrimaryRoleId] = useState("");
  const [additionalRoleIds, setAdditionalRoleIds] = useState([]);
  const [initialRoleIds, setInitialRoleIds] = useState([]);
  const [effectiveLoading, setEffectiveLoading] = useState(false);
  const [effectiveRoles, setEffectiveRoles] = useState([]);
  const [enabledFlags, setEnabledFlags] = useState([]);

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      setRoles(await fetchPermissionGroups());
    } catch (e) {
      console.error(e);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  const loadEffective = useCallback(async (id) => {
    if (!id) return;
    setEffectiveLoading(true);
    try {
      const data = await fetchUserPermissionsForUser(id);
      const ids = normalizePermissionGroupIds(data || {});
      setInitialRoleIds(ids);
      const primary = ids[0] ?? "";
      setPrimaryRoleId(primary ? String(primary) : "");
      setAdditionalRoleIds(ids.slice(1).map(String));

      const names =
        data?.permission_groups?.map((g) => g.name).filter(Boolean) ||
        (data?.permission_group_name
          ? String(data.permission_group_name).split(",").map((s) => s.trim())
          : []);
      setEffectiveRoles(names.length ? names : ids.map((i) => `Role #${i}`));

      const perms = data?.permissions || data;
      const flags =
        perms && typeof perms === "object" && !Array.isArray(perms)
          ? enabledPermissionLabels(perms)
          : enabledPermissionLabels(data);
      setEnabledFlags(flags);

      if (perms && typeof perms === "object") {
        onLegacyAccessDerived?.(deriveLegacyApplicationAccessFlags(perms));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setEffectiveLoading(false);
    }
  }, [onLegacyAccessDerived]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (userId) loadEffective(userId);
  }, [userId, loadEffective]);

  useEffect(() => {
    onRolesChange?.({
      primaryRoleId,
      additionalRoleIds,
      initialRoleIds,
    });
  }, [primaryRoleId, additionalRoleIds, initialRoleIds, onRolesChange]);

  const additionalOptions = useMemo(
    () =>
      roles.filter(
        (r) => String(r.id) !== String(primaryRoleId),
      ),
    [roles, primaryRoleId],
  );

  const toggleAdditional = (roleId) => {
    const id = String(roleId);
    setAdditionalRoleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const effectiveSummary =
    enabledFlags.length > 0
      ? `${enabledFlags.length} permission(s) enabled via assigned role(s)`
      : "No permissions enabled yet — assign a role and configure its permissions.";

  return (
    <div className="add-account-permissions-section space-y-4">
      <div>
        <p className="add-account-section-title">CRM roles & access</p>
        <p className="mt-1 text-xs text-gray-500">
          Choose a primary role for default access. Add secondary roles only when
          this employee needs extra modules. Same team, different access? Use
          another primary role, stack an additional role, or use Settings →
          Advanced exceptions.
        </p>
      </div>

      <div className={FIELD_CLASS}>
        <label htmlFor="employee-primary-role" className={LABEL_CLASS}>
          Primary role
        </label>
        <select
          id="employee-primary-role"
          className={INPUT_CLASS}
          value={primaryRoleId}
          disabled={disabled || rolesLoading}
          onChange={(e) => {
            const next = e.target.value;
            setPrimaryRoleId(next);
            setAdditionalRoleIds((prev) => prev.filter((id) => id !== next));
          }}
        >
          <option value="">
            {rolesLoading ? "Loading roles…" : "Select primary role"}
          </option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </div>

      <div className={FIELD_CLASS}>
        <span className={LABEL_CLASS}>Additional roles (optional)</span>
        <div className="mt-2 max-h-40 space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/80 p-3">
          {rolesLoading ? (
            <p className="text-xs text-gray-500">Loading…</p>
          ) : additionalOptions.length === 0 ? (
            <p className="text-xs text-gray-500">No other roles available.</p>
          ) : (
            additionalOptions.map((r) => (
              <label
                key={r.id}
                className="flex cursor-pointer items-center gap-2 text-sm text-gray-800"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={additionalRoleIds.includes(String(r.id))}
                  disabled={disabled}
                  onChange={() => toggleAdditional(r.id)}
                />
                {r.name}
              </label>
            ))
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50/90 p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
          Effective access
        </p>
        {effectiveLoading ? (
          <p className="mt-2 text-sm text-slate-500">Loading effective permissions…</p>
        ) : (
          <>
            <p className="mt-2 text-sm text-slate-800">
              <span className="font-medium">Roles: </span>
              {effectiveRoles.length
                ? effectiveRoles.join(", ")
                : "None assigned"}
            </p>
            <p className="mt-1 text-xs text-slate-600">{effectiveSummary}</p>
            {enabledFlags.length > 0 && enabledFlags.length <= 12 ? (
              <ul className="mt-2 max-h-32 list-disc space-y-0.5 overflow-y-auto pl-4 text-xs text-slate-700">
                {enabledFlags.slice(0, 12).map((f) => (
                  <li key={f.key}>{f.label}</li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/** Call after account save to persist role membership changes. */
export async function persistEmployeeRoleAssignment(userId, state) {
  if (!userId || !state) return;
  const target = buildTargetRoleIds(
    state.primaryRoleId,
    state.additionalRoleIds,
  );
  const previous = state.initialRoleIds || [];
  if (!target.length && !previous.length) return;
  try {
    await syncUserRoleMembership(userId, target, previous);
  } catch (e) {
    console.error(e);
    swal(
      "Roles",
      "Account saved but role assignment could not be updated. Try Roles & access or re-open this account.",
      "warning",
    );
  }
}
